import React, { useState, useEffect } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { toast } from 'react-toastify';
import { addPuzzle, ChessPuzzle } from '../../../../services/chessPuzzleService';

interface CreatePuzzleViewProps {
    serviceProviderId: string;
    loggedInUserId: string;
}

const CreatePuzzleView: React.FC<CreatePuzzleViewProps> = ({ serviceProviderId, loggedInUserId }) => {
    const [game, setGame] = useState<Chess>(new Chess());
    const [fen, setFen] = useState<string>('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
    const [solutionMoves, setSolutionMoves] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [hint, setHint] = useState<string>('');
    const [complexity, setComplexity] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
    const [category, setCategory] = useState<'tactics' | 'endgame' | 'opening' | 'middlegame' | 'strategy' | 'calculation' | 'pattern'>('tactics');
    const [themes, setThemes] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);

    // Update FEN when game changes
    useEffect(() => {
        setFen(game.fen());
    }, [game]);

    // Validate solution moves
    const validateSolution = (moves: string): { valid: boolean; moveCount: number; error?: string } => {
        if (!moves.trim()) {
            return { valid: false, moveCount: 0, error: 'Solution moves are required' };
        }

        const moveList = moves.trim().split(/\s+/);
        const testGame = new Chess(game.fen());

        for (let i = 0; i < moveList.length; i++) {
            try {
                testGame.move(moveList[i]);
            } catch (err) {
                return { 
                    valid: false, 
                    moveCount: i, 
                    error: `Invalid move at position ${i + 1}: ${moveList[i]}` 
                };
            }
        }

        return { valid: true, moveCount: moveList.length };
    };

    const handlePieceDrop = (sourceSquare: string, targetSquare: string) => {
        const gameCopy = new Chess(game.fen());
        const move = gameCopy.move({
            from: sourceSquare,
            to: targetSquare,
            promotion: 'q'
        });

        if (move) {
            setGame(gameCopy);
            return true;
        }
        return false;
    };

    const handleFenChange = (newFen: string) => {
        try {
            const testGame = new Chess(newFen);
            setGame(testGame);
            setFen(newFen);
        } catch (err) {
            toast.error('Invalid FEN notation');
        }
    };

    const handleResetBoard = () => {
        const newGame = new Chess();
        setGame(newGame);
        setFen(newGame.fen());
    };

    const handleSavePuzzle = async () => {
        // Validate inputs
        if (!description.trim()) {
            toast.error('Please enter a puzzle description');
            return;
        }

        const solutionValidation = validateSolution(solutionMoves);
        if (!solutionValidation.valid) {
            toast.error(solutionValidation.error || 'Invalid solution moves');
            return;
        }

        if (solutionValidation.moveCount === 0) {
            toast.error('Solution must have at least one move');
            return;
        }

        setIsSaving(true);

        try {
            const puzzle: Omit<ChessPuzzle, 'id' | 'createdAt'> = {
                fen: game.fen(),
                moves: solutionMoves.trim(),
                rating: complexity === 'beginner' ? 1200 : complexity === 'intermediate' ? 1600 : 2000,
                themes: themes.split(',').map(t => t.trim()).filter(t => t),
                description: description.trim(),
                hint: hint.trim() || undefined,
                complexity,
                category,
                source: 'custom'
            };

            const puzzleId = await addPuzzle(serviceProviderId, puzzle, loggedInUserId);

            if (puzzleId) {
                toast.success('✅ Puzzle created and saved successfully!');
                // Reset form
                setDescription('');
                setSolutionMoves('');
                setHint('');
                setThemes('');
                setComplexity('beginner');
                setCategory('tactics');
                handleResetBoard();
            }
        } catch (error) {
            console.error('Error saving puzzle:', error);
            toast.error('Failed to save puzzle');
        } finally {
            setIsSaving(false);
        }
    };

    const solutionValidation = validateSolution(solutionMoves);
    const moveCount = solutionValidation.moveCount;

    return (
        <div className="create-puzzle-container" style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <div className="create-puzzle-header" style={{ 
                padding: '1rem 1.5rem', 
                borderBottom: '1px solid #e5e7eb',
                background: '#f9fafb',
                flexShrink: 0
            }}>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: 0, marginBottom: '0.25rem' }}>
                    Create Puzzle
                </h1>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                    Set up a position and define the solution to create a new puzzle
                </p>
            </div>

            <div style={{ 
                flex: 1, 
                overflow: 'auto', 
                display: 'flex', 
                gap: '1.5rem',
                padding: '1.5rem'
            }}>
                {/* Left Panel - Board */}
                <div className="board-panel" style={{ 
                    flex: '0 0 500px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                }}>
                    <div style={{ 
                        background: 'white',
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        border: '1px solid #e5e7eb'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '1rem'
                        }}>
                            <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                                Position Setup
                            </h3>
                            <button
                                onClick={handleResetBoard}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: '#f3f4f6',
                                    color: '#374151',
                                    border: 'none',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                }}
                            >
                                Reset Board
                            </button>
                        </div>
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '1rem',
                            width: '100%',
                            maxWidth: '550px',
                            margin: '0 auto 1rem'
                        }}>
                            <Chessboard 
                                position={game.fen()} 
                                onPieceDrop={handlePieceDrop}
                                boardWidth={Math.min(550, window.innerWidth - 100)}
                            />
                        </div>
                        <div style={{ 
                            marginTop: '1rem',
                            padding: '0.75rem',
                            background: '#f9fafb',
                            borderRadius: '0.375rem'
                        }}>
                            <label style={{ 
                                display: 'block',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                color: '#374151',
                                marginBottom: '0.5rem'
                            }}>
                                FEN (Position)
                            </label>
                            <input
                                type="text"
                                value={fen}
                                onChange={(e) => handleFenChange(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.75rem',
                                    fontFamily: 'monospace',
                                    background: 'white'
                                }}
                                placeholder="rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
                            />
                        </div>
                    </div>
                </div>

                {/* Right Panel - Puzzle Details */}
                <div className="details-panel" style={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    overflowY: 'auto'
                }}>
                    <div style={{ 
                        background: 'white',
                        borderRadius: '0.5rem',
                        padding: '1.5rem',
                        border: '1px solid #e5e7eb'
                    }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', marginBottom: '1rem' }}>
                            Puzzle Details
                        </h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {/* Description */}
                            <div>
                                <label style={{ 
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Description <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="e.g., Back Rank Mate, Fork the King and Queen"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem'
                                    }}
                                />
                            </div>

                            {/* Solution Moves */}
                            <div>
                                <label style={{ 
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Solution Moves <span style={{ color: '#dc2626' }}>*</span>
                                    {moveCount > 0 && (
                                        <span style={{ 
                                            marginLeft: '0.5rem',
                                            fontSize: '0.75rem',
                                            color: solutionValidation.valid ? '#16a34a' : '#dc2626',
                                            fontWeight: 600
                                        }}>
                                            ({moveCount} {moveCount === 1 ? 'move' : 'moves'})
                                        </span>
                                    )}
                                </label>
                                <input
                                    type="text"
                                    value={solutionMoves}
                                    onChange={(e) => setSolutionMoves(e.target.value)}
                                    placeholder="e.g., e4 e5 Nf3 Nc6 Bb5 (use standard notation: e4, Nf3, Bb5, etc.)"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: `1px solid ${solutionValidation.valid || !solutionMoves ? '#d1d5db' : '#dc2626'}`,
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem',
                                        fontFamily: 'monospace',
                                        background: solutionValidation.valid || !solutionMoves ? 'white' : '#fef2f2'
                                    }}
                                />
                                {solutionMoves && !solutionValidation.valid && (
                                    <p style={{ 
                                        marginTop: '0.5rem',
                                        fontSize: '0.75rem',
                                        color: '#dc2626'
                                    }}>
                                        {solutionValidation.error}
                                    </p>
                                )}
                                {solutionValidation.valid && moveCount > 0 && (
                                    <p style={{ 
                                        marginTop: '0.5rem',
                                        fontSize: '0.75rem',
                                        color: '#16a34a'
                                    }}>
                                        ✓ Solution is valid ({moveCount} {moveCount === 1 ? 'move' : 'moves'})
                                    </p>
                                )}
                            </div>

                            {/* Hint */}
                            <div>
                                <label style={{ 
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Hint (Optional)
                                </label>
                                <textarea
                                    value={hint}
                                    onChange={(e) => setHint(e.target.value)}
                                    placeholder="e.g., Look for a back rank weakness"
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>

                            {/* Complexity and Category */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Difficulty
                                    </label>
                                    <select
                                        value={complexity}
                                        onChange={(e) => setComplexity(e.target.value as 'beginner' | 'intermediate' | 'advanced')}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '0.375rem',
                                            fontSize: '0.875rem',
                                            background: 'white',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="beginner">🟢 Beginner</option>
                                        <option value="intermediate">🟡 Intermediate</option>
                                        <option value="advanced">🔴 Advanced</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}>
                                        Category
                                    </label>
                                    <select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value as 'tactics' | 'endgame' | 'opening' | 'middlegame' | 'strategy' | 'calculation' | 'pattern')}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '0.375rem',
                                            fontSize: '0.875rem',
                                            background: 'white',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <option value="tactics">🎯 Tactics</option>
                                        <option value="opening">🚀 Opening</option>
                                        <option value="endgame">🏁 Endgame</option>
                                        <option value="middlegame">⚔️ Middlegame</option>
                                        <option value="strategy">🧠 Strategy</option>
                                        <option value="calculation">🔢 Calculation</option>
                                        <option value="pattern">🔍 Pattern Recognition</option>
                                    </select>
                                </div>
                            </div>

                            {/* Themes */}
                            <div>
                                <label style={{ 
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '0.5rem'
                                }}>
                                    Themes (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={themes}
                                    onChange={(e) => setThemes(e.target.value)}
                                    placeholder="e.g., fork, pin, back rank, sacrifice (comma-separated)"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem'
                                    }}
                                />
                            </div>

                            {/* Save Button */}
                            <div style={{ marginTop: '0.5rem' }}>
                                <button
                                    onClick={handleSavePuzzle}
                                    disabled={isSaving || !description.trim() || !solutionValidation.valid || moveCount === 0}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        background: (isSaving || !description.trim() || !solutionValidation.valid || moveCount === 0) 
                                            ? '#9ca3af' 
                                            : '#9333ea',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        cursor: (isSaving || !description.trim() || !solutionValidation.valid || moveCount === 0) 
                                            ? 'not-allowed' 
                                            : 'pointer'
                                    }}
                                >
                                    {isSaving ? 'Saving Puzzle...' : '✨ Save Puzzle'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreatePuzzleView;

